import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ALERT_TYPE } from 'src/app/constants';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { CheckIn } from 'src/model/checkIn.model';
import { FileService } from 'src/services/file.service';
import { MidMonthService } from 'src/services/mid-month.service';
import { ReportService } from 'src/services/report.service';

@Component({
    selector: 'app-mid-month-report',
    templateUrl: './mid-month-report.component.html',
    styleUrls: ['./mid-month-report.component.scss'],
    standalone: false
})

export class MidMonthReportComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  protected sharedService = inject(SharedService);
  private activeModal = inject(NgbActiveModal);
  private datePipe = inject(DatePipe);
  private midMonthService = inject(MidMonthService);
  private reportService = inject(ReportService);
  private fileService = inject(FileService);

  advanced = false;
  previousEndDates: Date[] = [];
  disabledSubmit = false;
  startDate: Date;
  endDate: Date;
  selectedFile: File;
  @Input() selectedCompanyID: number;
  routeSubscription: any;
  isCollapsed = true;

  willOmitSchemeA = false;
  willOmitInfo = false;
  withDLReport = true;

  useVersion1 = false;

  allowGeneration = true;

  ngOnInit(): void {
    this.getPreviousEndDates();
    this.routeSubscription = this.activatedRoute.paramMap.subscribe(params => {
      this.selectedCompanyID = + params.get('companyId');
      const eDate = params.get('endDate');
      if (eDate) {
        this.setRange(new Date(eDate));
        if (!this.getEndDateInRange()) this.advanced = true;
        this.submit();
      }
    });
  }

  getPreviousEndDates() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentMonth = today.getMonth();
    const todayDate = today.getDate();

    const startMonth = todayDate < this.midMonthService.defaultEndDate ? 1 : 0;

    for (let i = startMonth; i < startMonth + this.midMonthService.noOfPreviousMonth; i++) {
      const endDate = new Date(today);
      endDate.setMonth(currentMonth - i, this.midMonthService.defaultEndDate);
      this.previousEndDates.push(endDate);
    }

    this.setRange(this.previousEndDates[0]);
  }

  getEndDateInRange() {
    return (this.previousEndDates.some(d => d.getTime() === this.endDate.getTime()));
  }

  setRange(endDate: Date) {
    this.endDate = new Date(endDate);
    this.endDate.setHours(0, 0, 0, 0);
    const startDate = new Date(endDate);
    startDate.setMonth(startDate.getMonth() - 1, this.midMonthService.defaultStartDate);
    startDate.setHours(0, 0, 0, 0);
    this.startDate = startDate;
  }

  toggleAdvanced() {
    if (!this.advanced && !this.getEndDateInRange()) {
      this.startDate = undefined;
      this.endDate = undefined;
    }
  }

  isFieldValid() {
    return (this.startDate !== undefined && this.endDate !== undefined)
  }

  async submit() {
    if (this.isFieldValid()) {
      const err: string[] = [];
      if (! await this.compareLastCheckInDate(this.startDate))
        err.push(`START DATE (${this.datePipe.transform(this.startDate, 'yyyy/MM/dd')})`);

      if (! await this.compareLastCheckInDate(this.endDate))
        err.push(`END DATE (${this.datePipe.transform(this.endDate, 'yyyy/MM/dd')})`);

      if (err.length > 0)
        this.sharedService.openAlert(`Please verify your account on ${err.join(' and ')}`, ALERT_TYPE.DANGER);
      else {
        await this.generateMidMonthNew();
        this.midMonthService.saveMidMonthRange(this.startDate, this.endDate);
      }
    } else
      this.sharedService.openAlert("Please fill in all neccessary fields!", ALERT_TYPE.DANGER);
  }

  async generateMidMonthNew() {
    this.disabledSubmit = true;
    const startDateStr = this.sharedService.createLocalISODateEncodedString(this.startDate);
    const endDate = this.sharedService.getLastMsOfDate(this.endDate);
    const endDateStr = this.sharedService.createLocalISODateEncodedString(endDate);
    try {
      const d = await this.midMonthService.getMidMonth(this.selectedCompanyID, startDateStr, endDateStr, this.willOmitSchemeA, this.willOmitInfo);
      this.fileService.downloadFile(d);

      if (this.withDLReport == true) {
        const e = await this.midMonthService.getExtrasReport(this.selectedCompanyID, startDateStr, endDateStr);
        this.fileService.downloadFile(e);
      }

      const msg = (this.withDLReport) ? "Successfully generated MidMonth & Extras Report!" : "Successfully generated MidMonth Report!";
      this.sharedService.openAlert(msg, ALERT_TYPE.SUCCESS);
    } finally {
      this.disabledSubmit = false;
    }
  }

  async compareLastCheckInDate(aDate: Date): Promise<boolean> {
    aDate = this.sharedService.getLastMsOfDate(aDate);
    const checkIns: CheckIn[] = await this.reportService.getCompaniesLatestCheckIns(this.selectedCompanyID, aDate.toISOString());
    const found: CheckIn = checkIns.find(c => c.Date.getTime() === aDate.getTime());
    return (found !== undefined);
  }

  ngOnDestroy() {
    this.routeSubscription.unsubscribe();
  }

  reset() {
    this.activeModal.close();
  }
}
